import { ThunkAction } from "redux-thunk";
import { GlobalState } from "store/configure-store";
import { Dispatch } from "react";
import { MedicationEntryType } from "model/medication-entry.model";
import { PrescriptionType } from "model/prescription.model";
import {
  PrescriptionAction,
  PRESCRIPTIONS_CREATED,
  PRESCRIPTIONS_CREATED_ERROR,
  PRESCRIPTIONS_CREATED_R,
  PRESCRIPTIONS_CREATED_R_ERROR,
  PRESCRIPTIONS_CREATED_R_START,
  PRESCRIPTIONS_CREATED_START,
  PRESCRIPTIONS_CREATED_T,
  PRESCRIPTIONS_PDF,
  PRESCRIPTIONS_PDF_START,
} from "store/types/prescription.types";
import * as PrescriptionAPI from "api/prescription.api";
import { selectStsToken } from "store/reducers/user-context.reducer";
import { selectEHealthPassword } from "store/reducers/session.reducer";
import { dispatchResetNotification } from "store/actions/notification.actions";
import { Error, ErrorTypeEnum } from "model/error.model";
import { i18nUserContext } from "config/translations/I18n";
import {
  selectHelenaPatientId,
  selectSessionKey,
} from "store/reducers/helena.reducer";
import * as HelenaActions from "store/actions/helena.actions";

/** ---- ACTIONS ---- */
/** Create prescription on CC, Recipe and Helena */
export const createPrescriptionAll = (
  patientId: number,
  caregiverId: number,
  medicationEntries: Array<MedicationEntryType>,
  subContactId: number,
  canUseHelena: boolean
): ThunkAction<
  Promise<Array<PrescriptionType>>,
  GlobalState,
  any,
  PrescriptionAction
> => {
  return async (dispatch) => {
    const prescriptionsCC = await dispatch(
      createPrescriptionCC(
        patientId,
        caregiverId,
        medicationEntries,
        subContactId
      )
    );

    if (canUseHelena) {
      return Promise.resolve(
        await dispatch(
          await prescriptionsRE_HE(patientId, caregiverId, prescriptionsCC)
        )
      );
    } else {
      return Promise.resolve(
        await dispatch(
          await prescriptionsRE(patientId, caregiverId, prescriptionsCC)
        )
      );
    }
  };
};

/** Create prescription on CC */
export const createPrescriptionCC = (
  patientId: number,
  caregiverId: number,
  medicationEntries: Array<MedicationEntryType>,
  subContactId: number
): ThunkAction<
  Promise<Array<PrescriptionType>>,
  GlobalState,
  any,
  PrescriptionAction
> => {
  return async (dispatch) => {
    try {
      const transientPrescriptions = await PrescriptionAPI.prescriptionGenerate(
        patientId,
        caregiverId,
        medicationEntries
      );

      transientPrescriptions.forEach((prescription) =>
        Object.assign(prescription, { subContactsIDs: [subContactId] })
      );

      dispatchPrescriptionsCreatedTransient(transientPrescriptions)(dispatch);
      dispatchPrescriptionsCreatedStart()(dispatch);
      const prescriptions = await PrescriptionAPI.createPrescriptions(
        patientId,
        caregiverId,
        transientPrescriptions
      );
      dispatchPrescriptionsCreated(prescriptions)(dispatch);
      return Promise.resolve(prescriptions);
    } catch (e) {
      dispatchPrescriptionsCreatedError()(dispatch);
      throw e;
    }
  };
};

/** Create prescription on Recipe and Helena */
export const prescriptionsRE_HE = (
  patientId: number,
  caregiverId: number,
  prescriptions: Array<PrescriptionType>
): ThunkAction<
  Promise<Array<PrescriptionType>>,
  GlobalState,
  any,
  PrescriptionAction
> => {
  return async (dispatch) => {
    const prescriptionsR = await dispatch(
      await prescriptionsRE(patientId, caregiverId, prescriptions)
    );

    const prescriptionHE = await dispatch(
      await prescriptionsHE(patientId, caregiverId, prescriptionsR)
    );

    return Promise.resolve(prescriptionHE);
  };
};

export const prescriptionsPDF = (
  patientId: number,
  caregiverId: number,
  prescriptions: Array<PrescriptionType>
): ThunkAction<
  Promise<Array<PrescriptionType>>,
  GlobalState,
  any,
  PrescriptionAction
> => {
  return async (dispatch, getState) => {
    dispatchPrescriptionsPdfStart()(dispatch);
    const prescriptionsPdf = await PrescriptionAPI.generatePdf(
      patientId,
      caregiverId,
      prescriptions
    );
    dispatchPrescriptionsPdf(prescriptionsPdf)(dispatch);
    return Promise.resolve(prescriptionsPdf);
  };
};

export const prescriptionsHE = (
  patientId: number,
  caregiverId: number,
  prescriptions: Array<PrescriptionType>
): ThunkAction<
  Promise<Array<PrescriptionType>>,
  GlobalState,
  any,
  PrescriptionAction
> => {
  return async (dispatch, getState) => {
    const prescriptionPdf = await dispatch(
      prescriptionsPDF(patientId, caregiverId, prescriptions)
    );
    await dispatch(
      HelenaActions.createPrescriptionHE(
        patientId,
        caregiverId,
        selectSessionKey(getState().helena),
        selectHelenaPatientId(getState().helena),
        prescriptionPdf
      )
    );

    return Promise.resolve(prescriptionPdf);
  };
};

/** Create prescription on Recipe */
export const prescriptionsRE = (
  patientId: number,
  caregiverId: number,
  prescriptions: Array<PrescriptionType>
): ThunkAction<
  Promise<Array<PrescriptionType>>,
  GlobalState,
  any,
  PrescriptionAction
> => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const eHealthPassword = selectEHealthPassword(state);
      dispatchPrescriptionsCreatedRecipeStart(prescriptions)(dispatch);
      const rPrescriptions = await PrescriptionAPI.createAllOnRecipe(
        patientId,
        caregiverId,
        prescriptions.map((p) => p.id),
        selectStsToken(state),
        eHealthPassword
      );
      dispatchPrescriptionsCreatedRecipe(rPrescriptions)(dispatch);
      return Promise.resolve(rPrescriptions);
    } catch (e) {
      dispatchPrescriptionsCreatedRecipeError()(dispatch);
      dispatchResetNotification()(dispatch);
      return Promise.reject(
        new Error({
          type: ErrorTypeEnum.PRESCRIPTION_RECIPE,
          message: i18nUserContext.t("medicationFlow.error.prescriptionRE"),
        })
      );
    }
  };
};

/** ---- DISPATCHERS ---- */
const dispatchPrescriptionsCreatedRecipeStart = (
  prescriptions: Array<PrescriptionType>
) => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED_R_START, payload: prescriptions });
  };
};

const dispatchPrescriptionsCreatedRecipe = (
  prescriptions: Array<PrescriptionType>
) => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED_R, payload: prescriptions });
  };
};

const dispatchPrescriptionsCreatedRecipeError = () => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED_R_ERROR });
  };
};

const dispatchPrescriptionsPdfStart = () => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_PDF_START });
  };
};

const dispatchPrescriptionsPdf = (prescriptions: Array<PrescriptionType>) => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_PDF, payload: prescriptions });
  };
};

const dispatchPrescriptionsCreated = (
  prescriptions: Array<PrescriptionType>
) => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED, payload: prescriptions });
  };
};

const dispatchPrescriptionsCreatedStart = () => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED_START });
  };
};

const dispatchPrescriptionsCreatedTransient = (
  prescriptions: Array<PrescriptionType>
) => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED_T, payload: prescriptions });
  };
};

const dispatchPrescriptionsCreatedError = () => {
  return (dispatch: Dispatch<PrescriptionAction>) => {
    dispatch({ type: PRESCRIPTIONS_CREATED_ERROR });
  };
};
